/**
 * The slow-interaction section of the Dynamics board (L1): which layer makes
 * F&O slow, for whom Cato can see, over a lookback the reader picks.
 *
 * Form follows the design rules: classification over time is a stacked area
 * (composition over time), the share is a donut with the total in the hole,
 * rankings are "worst 5" grids whose rows drill into `/dynamics/investigate`
 * pre-filtered by form or site (§2, §4.1) — for readers the backend will let in.
 * For anyone else the rankings are still shown, as plain text with one sentence
 * saying who can open a person: the numbers are not per-user, only the drill is.
 *
 * A quiet section is ambiguous — "nothing was slow" and "the DEM collector has
 * not written since Tuesday" look identical on these charts — so the section
 * carries a door to the collectors instead of a copy of them. Ingest health,
 * browser coverage and collector freshness are the experience board's, on the
 * principle that a shared number has exactly one owner.
 *
 * Honesty: every ratio carries its coverage (the meter in the L0 tiles and the
 * caption on each card), UNATTRIBUTED is drawn on the neutral scale and named
 * as a coverage gap, folded cohorts are stated, `null` p95 is an em dash, and
 * nothing renders while `access_state` is not `ok` (§5).
 */

import { Chart } from "@norican/dataviz";
import { Gauge } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import { INVESTIGATOR_ONLY_LINE } from "@/components/nav-access";
// The experience board's own route constant — one string, no cycle: that module
// imports nothing at all.
import { EXPERIENCE_PATH } from "@/features/experience/params";
import {
  type FoConsoleLookback,
  type FoConsoleSlowSummary,
  type FoConsoleWorstForm,
  type FoConsoleWorstSite,
  useFoInvestigatorAccess,
} from "@/lib/api";
import { DrillLink, useDrillNavigate } from "@/lib/drill";

import {
  CLASSIFICATION_CONFIG,
  CLASSIFICATION_ORDER,
  ClassificationChip,
  classificationColor,
} from "./console-chips";
import {
  CLASSIFICATION_LABEL,
  type ClassificationFamily,
  classificationFamily,
  formatCoverage,
  formatDurationMs,
  heatBand,
  lookbackOption,
} from "./console-format";
import { BoardCrossLink } from "./cross-link";
import { PanelCard } from "./panel-card";
import {
  DYNAMICS_INVESTIGATE_PATH,
  FORM_PARAM,
  LOOKBACK_PARAM,
  SITE_PARAM,
  SLOW_ANCHOR,
} from "./params";
import { formatBinDateTime, formatBinTime, formatCount, tooltipValue } from "./status";
import { UnattributedOriginsCard } from "./unattributed-origins";

/** How many rows the L1 grids rank — the rest live one click deeper. */
const WORST_LIMIT = 5;

interface SlowSectionProps {
  summary: FoConsoleSlowSummary;
  lookback: FoConsoleLookback;
}

/** One hour of the stacked area: the bucket, its axis label, and a count per family present. */
type HourRow = { ts: string; label: string } & Partial<Record<ClassificationFamily, number>>;

/** The families present in the payload, in fixed legend order. */
function familiesPresent(summary: FoConsoleSlowSummary): ClassificationFamily[] {
  const seen = new Set<ClassificationFamily>();
  for (const share of summary.shares) {
    seen.add(classificationFamily(share.classification));
  }
  for (const point of summary.by_hour) {
    seen.add(classificationFamily(point.classification));
  }
  return CLASSIFICATION_ORDER.filter((family) => seen.has(family));
}

export function SlowSection({ summary, lookback }: SlowSectionProps) {
  const navigateDrill = useDrillNavigate();
  // Whether this reader may open a per-person view at all. Derived from the
  // API's own answer, never from a list of addresses here — see
  // `lib/api/hooks/fo-console.ts`. A reader the backend would refuse gets the
  // rankings as plain text plus one sentence, not a row that 403s on click.
  const investigator = useFoInvestigatorAccess().access;
  const canInvestigate = investigator !== "denied";
  const staleAfterMs = summary.cache_ttl_sec * 2000;
  const windowCaption = lookbackOption(lookback).caption;
  const coverageText = formatCoverage(summary.coverage);
  const families = familiesPresent(summary);
  const longWindow = lookbackOption(lookback).ms > 24 * 3_600_000;

  // Shares folded to families, fixed order, zero-count families dropped.
  const shareByFamily = new Map<ClassificationFamily, { count: number; users: number }>();
  for (const share of summary.shares) {
    const family = classificationFamily(share.classification);
    const current = shareByFamily.get(family) ?? { count: 0, users: 0 };
    shareByFamily.set(family, {
      count: current.count + share.count,
      users: current.users + share.users,
    });
  }
  const donutSlices = families
    .map((family) => ({ family, ...(shareByFamily.get(family) ?? { count: 0, users: 0 }) }))
    .filter((slice) => slice.count > 0);

  // Hourly points pivoted to one row per hour with a column per family. An hour
  // with no rows is absent — not padded to zero — so a collector gap is a gap.
  const hourRows: HourRow[] = (() => {
    const byHour = new Map<string, HourRow>();
    for (const point of summary.by_hour) {
      const row = byHour.get(point.ts) ?? { ts: point.ts, label: "" };
      const family = classificationFamily(point.classification);
      row[family] = (row[family] ?? 0) + point.count;
      byHour.set(point.ts, row);
    }
    return [...byHour.values()]
      .sort((a, b) => a.ts.localeCompare(b.ts))
      .map((row) => ({
        ...row,
        label: longWindow ? formatBinDateTime(row.ts) : formatBinTime(row.ts),
      }));
  })();

  // Five labelled forms, then the k-anonymity fold if the backend sent one —
  // the fold is stated, never sliced away (same shape as the experience section).
  const labelledForms = summary.worst_forms.filter((row) => !row.is_other);
  const otherForm = summary.worst_forms.find((row) => row.is_other);
  const worstForms = [...labelledForms.slice(0, WORST_LIMIT), ...(otherForm ? [otherForm] : [])];
  const worstSites = summary.worst_sites.slice(0, WORST_LIMIT);
  const formP95s = worstForms.map((row) => row.p95_ms).filter((v): v is number => v != null);
  const formP95Min = formP95s.length > 0 ? Math.min(...formP95s) : 0;
  const formP95Max = formP95s.length > 0 ? Math.max(...formP95s) : 0;
  const siteTtfbs = worstSites.map((row) => row.ttfb_p50_ms).filter((v): v is number => v != null);
  const siteTtfbMin = siteTtfbs.length > 0 ? Math.min(...siteTtfbs) : 0;
  const siteTtfbMax = siteTtfbs.length > 0 ? Math.max(...siteTtfbs) : 0;

  const suppressionText =
    summary.suppression.folded === 0
      ? `No cohort fell below the k≥${summary.suppression.min_users} floor.`
      : `${formatCount(summary.suppression.folded)} cohorts used by fewer than ${summary.suppression.min_users} people were folded out of the rankings below.`;

  return (
    <section id={SLOW_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ChartCard<HourRow>
            title="Slow interactions by hour, by classification"
            caption={`${formatCount(summary.total)} slow, ${windowCaption} · network coverage ${coverageText} · an hour with no rows is a gap, not zero`}
            since={summary.collected_at}
            staleAfterMs={staleAfterMs}
            tableTwin={{
              id: "fo-slow-by-hour",
              ariaLabel: "Slow interactions per hour and classification family",
              data: hourRows,
              getRowId: (row) => row.ts,
              columns: [
                { id: "hour", header: "Hour", accessor: (row) => row.label },
                ...families.map((family) => ({
                  id: family,
                  header: CLASSIFICATION_LABEL[family],
                  align: "end" as const,
                  accessor: (row: HourRow) => row[family] ?? null,
                  cell: (row: HourRow) => (
                    <span className="tabular-nums">{formatCount(row[family] ?? null)}</span>
                  ),
                })),
              ],
            }}
          >
            {hourRows.length === 0 ? (
              <EmptyState
                size="sm"
                title="No slow interaction in this lookback"
                description="The saved function returned no row. Nothing was slow by the §5 rule, or the collectors have not written the window yet — the strip below says which."
              />
            ) : (
              <Chart.Container
                config={CLASSIFICATION_CONFIG}
                className="h-72"
                aria-label={`Slow interactions per hour stacked by classification family, ${windowCaption}`}
              >
                <Chart.AreaChart data={hourRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                  <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                  <Chart.YAxis {...Chart.axisProps} width={48} allowDecimals={false} />
                  <Chart.Tooltip
                    content={
                      <Chart.TooltipContent
                        formatter={(value) => formatCount(tooltipValue(value))}
                      />
                    }
                  />
                  <Chart.Legend content={<Chart.LegendContent />} />
                  {families.map((family) => (
                    <Chart.Area
                      key={family}
                      {...Chart.seriesDefaults}
                      dataKey={family}
                      name={CLASSIFICATION_LABEL[family]}
                      stackId="classification"
                      type="monotone"
                      stroke={classificationColor(family)}
                      fill={classificationColor(family)}
                      fillOpacity={0.55}
                      connectNulls={false}
                    />
                  ))}
                </Chart.AreaChart>
              </Chart.Container>
            )}
          </ChartCard>
        </div>

        <div className="lg:col-span-2">
          <ChartCard<(typeof donutSlices)[number]>
            title="Classification share"
            caption={`${formatCount(summary.users)} users · coverage ${coverageText}`}
            since={summary.collected_at}
            staleAfterMs={staleAfterMs}
            tableTwin={{
              id: "fo-slow-shares",
              ariaLabel: "Slow interactions per classification family",
              data: donutSlices,
              getRowId: (row) => row.family,
              columns: [
                {
                  id: "family",
                  header: "Family",
                  accessor: (row) => CLASSIFICATION_LABEL[row.family],
                },
                {
                  id: "count",
                  header: "Slow",
                  align: "end",
                  accessor: (row) => row.count,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
                },
                {
                  id: "users",
                  header: "Users",
                  align: "end",
                  accessor: (row) => row.users,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
                },
              ],
            }}
          >
            {donutSlices.length === 0 ? (
              <EmptyState
                size="sm"
                title="Nothing to classify"
                description="No slow interaction in the window."
              />
            ) : (
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Chart.Container
                    config={CLASSIFICATION_CONFIG}
                    className="h-56"
                    aria-label={`Share of slow interactions by classification family, ${windowCaption}`}
                  >
                    <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <Chart.Tooltip
                        content={
                          <Chart.TooltipContent
                            formatter={(value) => `${formatCount(tooltipValue(value))} slow`}
                            hideLabel
                          />
                        }
                      />
                      <Chart.Pie
                        {...Chart.seriesDefaults}
                        data={donutSlices}
                        dataKey="count"
                        nameKey="family"
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="85%"
                        paddingAngle={2}
                        cornerRadius={3}
                        stroke="var(--norican-surface)"
                        strokeWidth={2}
                      >
                        {donutSlices.map((slice) => (
                          <Chart.Cell key={slice.family} fill={classificationColor(slice.family)} />
                        ))}
                      </Chart.Pie>
                    </Chart.PieChart>
                  </Chart.Container>
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                  >
                    <Statistic
                      label="Slow"
                      value={formatCount(summary.total)}
                      size="lg"
                      className="text-center"
                    />
                  </div>
                </div>
                <div className="text-fg-muted flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs">
                  {donutSlices.map((slice) => (
                    <span key={slice.family} className="inline-flex items-center gap-1.5">
                      <ClassificationChip classification={slice.family} />
                      {formatCount(slice.count)}
                    </span>
                  ))}
                </div>
                <Typography variant="caption" tone="muted">
                  Unattributed is a coverage gap, not a finding: DKTAA and CZPRI do not break out
                  through Cato yet, so their users have no network layer here.
                </Typography>
              </div>
            )}
          </ChartCard>
        </div>
      </div>

      {/* The unattributed slice, explained: where those people sit (Entra) — and
          beside it a door to the other thing that shrinks it, which lives on the
          experience board rather than being drawn twice here. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <UnattributedOriginsCard summary={summary} lookback={lookback} />
        </div>
        <BoardCrossLink
          title="Browser coverage and data sources"
          caption="How much of the fleet reports through the extension, and whether the DEM and identity collectors are current, are the experience board's numbers. A quiet section here is either a calm week or a stale collector — that board says which."
          to={EXPERIENCE_PATH}
          cta="Application experience"
          icon={Gauge}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PanelCard
          title="Worst forms"
          caption={
            canInvestigate ? (
              <DrillLink
                to={DYNAMICS_INVESTIGATE_PATH}
                params={{ [LOOKBACK_PARAM]: lookback }}
                className="text-accent text-sm font-medium hover:underline"
              >
                Investigate a user →
              </DrillLink>
            ) : (
              INVESTIGATOR_ONLY_LINE
            )
          }
          padding={worstForms.length === 0 ? "md" : "none"}
        >
          {worstForms.length === 0 ? (
            <EmptyState
              size="sm"
              title="No form ranked"
              description="No slow interaction in the window."
            />
          ) : (
            <DataGrid<FoConsoleWorstForm>
              data={worstForms}
              getRowId={(row) => `${row.is_other ? "other" : row.form}|${row.legal_entity}`}
              density="compact"
              aria-label={`The ${WORST_LIMIT} forms with the most slow interactions`}
              columns={[
                {
                  id: "form",
                  header: "Form",
                  accessor: (row) => (row.is_other ? "Other" : row.form),
                  cell: (row) =>
                    // The k-anonymity fold: a row that stands for several forms
                    // used by too few people to name. Not a door — there is no
                    // single form to investigate.
                    row.is_other ? (
                      <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                        Other
                        <Badge tone="neutral" variant="outline" size="xs">
                          folded cohort
                        </Badge>
                      </span>
                    ) : canInvestigate ? (
                      <button
                        type="button"
                        className="text-fg rounded text-left text-sm font-medium hover:underline focus-visible:outline-2"
                        onClick={() =>
                          navigateDrill(DYNAMICS_INVESTIGATE_PATH, {
                            [FORM_PARAM]: row.form,
                            [LOOKBACK_PARAM]: lookback,
                          })
                        }
                      >
                        {row.form}
                      </button>
                    ) : (
                      // Not a button: the drill it would open is a per-person
                      // view the backend refuses for this sign-in, and a row
                      // that navigates to a 403 is a dead door with extra steps.
                      <span className="text-fg text-sm font-medium">{row.form}</span>
                    ),
                },
                { id: "entity", header: "Entity", accessor: (row) => row.legal_entity },
                {
                  id: "count",
                  header: "Slow",
                  align: "end",
                  accessor: (row) => row.count,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
                },
                {
                  id: "users",
                  header: "Users",
                  align: "end",
                  accessor: (row) => row.users,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
                },
                {
                  id: "p95",
                  header: "P95",
                  align: "end",
                  accessor: (row) => row.p95_ms,
                  cell: (row) => (
                    <span className="tabular-nums">{formatDurationMs(row.p95_ms)}</span>
                  ),
                  cellScale: (value) =>
                    heatBand(typeof value === "number" ? value : null, formP95Min, formP95Max),
                  cellScaleRamp: "danger",
                },
              ]}
            />
          )}
        </PanelCard>

        <PanelCard
          title="Worst sites"
          caption={`only users Cato sees · coverage ${coverageText}`}
          padding={worstSites.length === 0 ? "md" : "none"}
        >
          {worstSites.length === 0 ? (
            <EmptyState
              size="sm"
              title="No site ranked"
              description="No slow interaction from a user Cato can see. With DKTAA and CZPRI not behind Cato, this is expected to be a short list."
            />
          ) : (
            <DataGrid<FoConsoleWorstSite>
              data={worstSites}
              getRowId={(row) => row.site}
              density="compact"
              aria-label={`The ${WORST_LIMIT} Cato sites with the most slow interactions`}
              columns={[
                {
                  id: "site",
                  header: "Site",
                  accessor: (row) => row.site,
                  cell: (row) =>
                    canInvestigate ? (
                      <button
                        type="button"
                        className="text-fg rounded text-left text-sm font-medium hover:underline focus-visible:outline-2"
                        onClick={() =>
                          navigateDrill(DYNAMICS_INVESTIGATE_PATH, {
                            [SITE_PARAM]: row.site,
                            [LOOKBACK_PARAM]: lookback,
                          })
                        }
                      >
                        {row.site}
                      </button>
                    ) : (
                      <span className="text-fg text-sm font-medium">{row.site}</span>
                    ),
                },
                {
                  id: "count",
                  header: "Slow",
                  align: "end",
                  accessor: (row) => row.count,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
                },
                {
                  id: "users",
                  header: "Users",
                  align: "end",
                  accessor: (row) => row.users,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
                },
                {
                  id: "ttfb",
                  header: "TTFB p50",
                  align: "end",
                  accessor: (row) => row.ttfb_p50_ms,
                  cell: (row) => (
                    <span className="tabular-nums">{formatDurationMs(row.ttfb_p50_ms)}</span>
                  ),
                  cellScale: (value) =>
                    heatBand(typeof value === "number" ? value : null, siteTtfbMin, siteTtfbMax),
                  cellScaleRamp: "info",
                },
                {
                  id: "degraded",
                  header: "Degraded",
                  align: "end",
                  accessor: (row) => row.degraded,
                  cell: (row) => (
                    <span
                      className={row.degraded > 0 ? "text-warning-fg tabular-nums" : "tabular-nums"}
                    >
                      {formatCount(row.degraded)}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </PanelCard>
      </div>

      <Typography variant="body-sm" tone="muted">
        {suppressionText} {formatCount(summary.mapped)} of {formatCount(summary.total)} slow
        interactions carry an identity (the alias join); the rest are shared or system accounts and
        never enter a per-user view.
      </Typography>
    </section>
  );
}
